export function toDatetimeLocalValue(date = new Date()) {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

/** Earliest departure a customer can choose: the current local minute. */
export function minDepartureLocalValue(now = new Date()) {
  return toDatetimeLocalValue(now);
}

/**
 * datetime-local departure at or after the current minute.
 * Empty, partial, or past values become now.
 */
export function clampDepartureLocalValue(value, now = new Date()) {
  const min = minDepartureLocalValue(now);
  if (typeof value !== "string") return min;
  const local = value.trim().slice(0, 16);
  if (local.length < 16) return min;
  return local < min ? min : local;
}

/** Date, hour, and minute for the departure picker, never before the current minute. */
export function departurePickerParts(value, now = new Date()) {
  const min = minDepartureLocalValue(now);
  const local = clampDepartureLocalValue(value, now);
  return {
    date: local.slice(0, 10),
    hour: local.slice(11, 13),
    minute: local.slice(14, 16),
    minDate: min.slice(0, 10),
    minHour: Number(min.slice(11, 13)),
    minMinute: Number(min.slice(14, 16)),
  };
}

/** Past hours on today are disabled. Future days keep every hour. */
export function isDepartureHourDisabled(date, hour, now = new Date()) {
  const min = minDepartureLocalValue(now);
  if (String(date) > min.slice(0, 10)) return false;
  return Number(hour) < Number(min.slice(11, 13));
}

/** Past minutes are disabled on the current hour of today. */
export function isDepartureMinuteDisabled(date, hour, minute, now = new Date()) {
  const min = minDepartureLocalValue(now);
  if (String(date) > min.slice(0, 10)) return false;
  const minHour = Number(min.slice(11, 13));
  const hourNum = Number(hour);
  if (hourNum > minHour) return false;
  if (hourNum < minHour) return true;
  return Number(minute) < Number(min.slice(14, 16));
}

export function fromDatetimeLocalValue(value) {
  if (!value) return new Date().toISOString();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}

export function travelSlotFilterKey(pickupWindow, date) {
  return pickupWindow && date ? `${pickupWindow}|${date}` : "";
}

export function parseTravelSlotFilterKey(key) {
  if (!key) return null;
  const sep = key.indexOf("|");
  if (sep <= 0) return null;
  return {
    pickupWindow: key.slice(0, sep),
    date: key.slice(sep + 1),
  };
}

/** True when departure is more than 15 minutes ahead (legacy pre-order threshold). */
export function isExplicitDriveDeparture(userDepartureIso) {
  if (!userDepartureIso) return false;
  const departure = new Date(userDepartureIso);
  if (Number.isNaN(departure.getTime())) return false;
  return departure.getTime() - Date.now() > 15 * 60 * 1000;
}

/** Resolve departure for API / slot math from leave-now vs user-picked mode. */
export function resolveDepartureIso(departureLocalValue, leaveNowMode = true) {
  const nowIso = new Date().toISOString();
  if (leaveNowMode) return nowIso;
  const iso = fromDatetimeLocalValue(clampDepartureLocalValue(departureLocalValue));
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < Date.now()) return nowIso;
  return iso;
}

/** @deprecated Use resolveDepartureIso(departureLocalValue, leaveNowMode) */
export function resolveLiveDepartureIso(departureLocalValue) {
  const iso = fromDatetimeLocalValue(departureLocalValue);
  return isExplicitDriveDeparture(iso) ? iso : new Date().toISOString();
}
